import React, { useEffect, useState } from "react";
import { Pill } from "../order/style";
import {
  Alert,
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  UncontrolledTooltip,
} from "reactstrap";
import { updateOrderStatus } from "./service";
import ConfirmationModal from "../project/ConfirmationModal";
import { formatDateWithSuffix } from "../common/DateHelpers";
import LoaderModal from "../common/LoaderModal";

const OrderInfo = ({
  projectSummary,
  setProjectSummary,
  setCurrentTab,
  reloadRoom,
  isMobile,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [nextStatus, setNextStatus] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationContent, setConfirmationContent] = useState({
    heading: "",
    message: "",
    func: null,
  });
  const [loaderModalInfo, setLoaderModalInfo] = useState({
    open: false,
    content: "",
  });
  const statusTextMapper = {
    finalised: {
      onConfirm: "Request Validation",
      onSuccess: "Requesting Validation",
    },
    confirmed: {
      onConfirm: "Place Order",
      onSuccess: "Placing Order",
    },
  };

  const toggleModal = () => setShowModal(!showModal);

  const toggleConfirmation = () => setConfirmationOpen(!confirmationOpen);

  useEffect(() => {
    if (!showModal) setNextStatus(null);
  }, [showModal]);

  const changeOrderStatus = (status) => {
    if (["finalised", "confirmed"].includes(status)) {
      if (
        confirm(
          `Are you sure you want to ${statusTextMapper[status].onConfirm}?`
        )
      ) {
        toggleModal();
        setLoaderModalInfo({
          open: true,
          content: `${statusTextMapper[status].onSuccess}. Please wait...`,
        });

        updateOrderStatus({
          projectId: projectSummary.id,
          orderId: projectSummary.order_id,
          status,
        }).then((res) => {
          setProjectSummary(res);
          reloadRoom();
          setNextStatus(null);
          setLoaderModalInfo({
            open: false,
            content: "",
          });
        });
      }
    } else {
      updateOrderStatus({
        projectId: projectSummary.id,
        orderId: projectSummary.order_id,
        status,
      }).then((res) => {
        setProjectSummary(res);
        reloadRoom();
      });
    }
  };

  return (
    <>
      {!isMobile && projectSummary.order_status && (
        <Pill className="me-2 pointer" onClick={() => setCurrentTab("order")}>
          {projectSummary.order_status}
        </Pill>
      )}
      {(() => {
        const approve = projectSummary.action_buttons.find(
          (e) => e.new_status === "confirmed"
        );
        const actionButton = projectSummary.action_buttons[0];

        if (approve)
          return (
            <>
              <div id="place_order_btn">
                <Button
                  color="primary"
                  className="rounded-1 fw-bold"
                  onClick={toggleModal}
                  disabled={projectSummary.is_price_stale || projectSummary.has_any_module_errors}
                >
                  {projectSummary.action_button_text || approve.text}
                </Button>
              </div>
              {projectSummary.is_price_stale && (
                <UncontrolledTooltip
                  placement="bottom"
                  target="place_order_btn"
                >
                  Refresh Price to place order
                </UncontrolledTooltip>
              )}
              <Modal
                backdrop="static"
                isOpen={showModal}
                toggle={toggleModal}
                size="lg"
              >
                <div
                  className="d-flex justify-content-between p-3 rounded-1 text-white fw-bold"
                  style={{ background: "#283039" }}
                >
                  <p className="m-0 fs-6">
                    {nextStatus?.new_status === "finalised"
                      ? "Validation"
                      : "Place Order"}
                  </p>
                  <div
                    onClick={toggleModal}
                    className="d-flex align-items-center pointer"
                  >
                    <i className="fa-solid fa-close fs-6" />
                  </div>
                </div>
                <ModalBody className="p-0">
                  {nextStatus ? (
                    nextStatus.new_status === "confirmed" ? (
                      <PlaceOrderModal
                        nextStatus={nextStatus}
                        setNextStatus={setNextStatus}
                        changeOrderStatus={changeOrderStatus}
                        currentStatus={projectSummary.actual_status}
                        deliveryDate={projectSummary.planned_delivery_date}
                      />
                    ) : (
                      <>
                        <MoveToApprovalModal
                          nextStatus={nextStatus}
                          setNextStatus={setNextStatus}
                          changeOrderStatus={changeOrderStatus}
                          deliveryDate={projectSummary.planned_delivery_date}
                        />
                      </>
                    )
                  ) : projectSummary.actual_status === "draft" ? (
                    <>
                      <div className="p-3 pb-0">
                        <p>Would you like to opt for validation?</p>
                        <p>
                          A basic check will be done to flag any issues in your
                          design. This is optional and usually takes 2 or more
                          days, based on complexity and team availability.
                        </p>
                      </div>
                      <div
                        className="d-flex justify-content-end px-3 py-2"
                        style={{ background: "#EEEEEE" }}
                      >
                        <div className="d-flex flex-row-reverse column-gap-2">
                          {projectSummary.action_buttons.map((e, index) => (
                            <Button
                              color={index === 1 ? "secondary" : "primary"}
                              outline={index === 1 ? true : false}
                              className="fw-bold rounded-1"
                              onClick={() => setNextStatus(e)}
                            >
                              {e.text}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <PlaceOrderModal
                      nextStatus={actionButton}
                      changeOrderStatus={changeOrderStatus}
                      currentStatus={projectSummary.actual_status}
                      toggleModal={toggleModal}
                      deliveryDate={projectSummary.planned_delivery_date}
                    />
                  )}
                </ModalBody>
              </Modal>
            </>
          );
        else if (projectSummary.action_buttons.length === 1)
          return (
            <Button
              color="primary"
              className="rounded-1 fw-bold"
              disabled={actionButton.disabled}
              onClick={() => {
                setConfirmationContent({
                  heading: actionButton.heading,
                  message: actionButton.message,
                  func: () => changeOrderStatus(actionButton.new_status),
                });
                toggleConfirmation();
              }}
            >
              {actionButton.text}
            </Button>
          );
      })()}
      <ConfirmationModal
        isOpen={confirmationOpen}
        toggle={toggleConfirmation}
        onConfirm={confirmationContent["func"]}
        message={confirmationContent["message"]}
        headingText={confirmationContent["heading"]}
      />
      <LoaderModal
        open={loaderModalInfo.open}
        content={loaderModalInfo.content}
      />
    </>
  );
};

const MoveToApprovalModal = ({
  nextStatus,
  setNextStatus,
  changeOrderStatus,
  deliveryDate,
}) => {
  const [checkbox, setCheckbox] = useState(false);

  return (
    <>
      <div className="p-3">
        <h6 className="mb-3">Validation Terms</h6>
        <ol className="d-flex flex-column row-gap-2 ps-3">
          <li>
            Your design can be sent for basic validation, which usually takes
            around 2 working days. Based on this, your tentative delivery date
            is <b>{formatDateWithSuffix(deliveryDate)}</b>, and the final date
            will be shared once the order is confirmed.
          </li>
          <li>
            During validation, we'll do a broad check covering key elements like
            cabinet structure, exposed sides, and layout. While we'll flag
            anything that clearly stands out, you're responsible for checking
            measurements, cabinet list, and final selections.
          </li>
          <li>
            We'll do our best to catch any issues, but since the design is added
            by you, some details may go unnoticed. Final responsibility stays
            with you.
          </li>
          <li>
            In some cases, validation may take longer depending on the design
            complexity or team availability.
          </li>
          <li>
            You can choose to skip the validation and place your order anytime.
          </li>
        </ol>
        <div className="d-flex column-gap-2">
          <Input
            className="m-0 border-secondary fs-6 rounded-1"
            type="checkbox"
            id="approval-confirmation"
            checked={checkbox}
            onChange={(e) => setCheckbox(e.target.checked)}
          />
          <Label className="m-0 fw-bold" for="approval-confirmation">
            I agree to all the terms.
          </Label>
        </div>
      </div>
      <div
        className="d-flex justify-content-end px-3 py-2"
        style={{ background: "#EEEEEE" }}
      >
        <div className="d-flex column-gap-2">
          <Button
            outline
            className="fw-bold rounded-1"
            onClick={() => setNextStatus(null)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className="fw-bold rounded-1"
            onClick={() => changeOrderStatus(nextStatus.new_status)}
            disabled={!checkbox}
          >
            {nextStatus.text}
          </Button>
        </div>
      </div>
    </>
  );
};

const PlaceOrderModal = ({
  nextStatus,
  setNextStatus,
  changeOrderStatus,
  currentStatus,
  toggleModal,
  deliveryDate,
}) => {
  const [checkboxes, setCheckboxes] = useState({
    delivery: false,
    scope: false,
    payment: false,
    unloading: false,
    support: false,
  });

  const handleCheckboxChange = (key) => {
    setCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allChecked = Object.values(checkboxes).every(Boolean);

  return (
    <>
      <div className="p-3">
        {nextStatus.disabled ? (
          <Alert color="warning" className="text-black m-0">
            <span className="fw-bold">
              A few laminates in your project are still awaiting approval.{" "}
            </span>
            <br />
            <span>
              Please contact the Boxs team or check back later to place the
              order.
            </span>
          </Alert>
        ) : (
          <>
            <div>
              <h6>Delivery Timeline</h6>
              <p>
                Your estimated delivery date is{" "}
                <b>{formatDateWithSuffix(deliveryDate)}</b>. While we aim to
                deliver on time, minor delays may happen due to material
                shortages, weather, or equipment issues. You'll be kept informed
                of any changes.
              </p>
              <InputGroup
                id="delivery-confirmation"
                checked={checkboxes.delivery}
                handleCheckboxChange={() => handleCheckboxChange("delivery")}
                text="I understand the delivery date is estimated and may shift if
            needed."
              />
            </div>
            <hr></hr>
            <div>
              <h6>Scope & Accuracy</h6>
              <p>
                Products will be made exactly as per the details entered in the
                Boxs app. Even if validation is used, the final output will
                follow your inputs. You are responsible for reviewing and
                confirming accuracy.
              </p>
              <InputGroup
                id="scope-confirmation"
                checked={checkboxes.scope}
                handleCheckboxChange={() => handleCheckboxChange("scope")}
                text="I confirm the app inputs are correct and final."
              />
            </div>
            <hr></hr>
            <div>
              <h6>Payments</h6>
              <p>
                A 50% advance is needed to begin processing. The balance is due
                before dispatch. For orders below ₹25,000, full payment is
                required before dispatch - no advance needed. Credit terms will
                apply if agreed.
              </p>
              <InputGroup
                id="payment-confirmation"
                checked={checkboxes.payment}
                handleCheckboxChange={() => handleCheckboxChange("payment")}
                text="I agree to the payment terms shared above."
              />
            </div>
            <hr></hr>
            <div>
              <h6>Transportation & Unloading</h6>
              <p>
                Products will be delivered by third-party partners, and charges
                are paid directly to them. Unloading isn't included by Boxs, but
                can be coordinated with the delivery partner if needed.
              </p>
              <InputGroup
                id="unloading-confirmation"
                checked={checkboxes.unloading}
                handleCheckboxChange={() => handleCheckboxChange("unloading")}
                text="I understand delivery and unloading are handled separately, and I'll coordinate if required."
              />
            </div>
            <hr></hr>
            <div>
              <h6>Post-Delivery Support</h6>
              <p>
                Support will be provided for site-related issues as per our
                standard terms. Ensuring the accuracy of inputs in the app is
                your responsibility.
              </p>
              <InputGroup
                id="support-confirmation"
                checked={checkboxes.support}
                handleCheckboxChange={() => handleCheckboxChange("support")}
                text="I accept responsibility for the inputs and agree to the support terms."
              />
            </div>
            {currentStatus !== "approved" && (
              <Alert color="warning" className="text-black mt-3">
                <span className="fw-bold">
                  {currentStatus === "finalised"
                    ? "Validation is currently in progress."
                    : "Please note that you're placing the order directly and have chosen not to use our validation service."}
                </span>
                <br />
                {currentStatus === "finalised" && (
                  <span>
                    If you choose to place the order now, the ongoing review
                    will be cancelled and your design will be processed as it
                    is.
                  </span>
                )}
              </Alert>
            )}
          </>
        )}
      </div>
      <div
        className="d-flex justify-content-end px-3 py-2"
        style={{ background: "#EEEEEE" }}
      >
        <div className="d-flex column-gap-2">
          <Button
            outline
            className="fw-bold rounded-1"
            onClick={() =>
              setNextStatus ? setNextStatus(null) : toggleModal()
            }
          >
            Cancel
          </Button>
          {!nextStatus.disabled && (
            <Button
              color="primary"
              className="fw-bold rounded-1"
              onClick={() => changeOrderStatus(nextStatus.new_status)}
              disabled={!allChecked}
            >
              {currentStatus === "finalised"
                ? "Skip Validation & Place Order"
                : nextStatus.text}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

const InputGroup = ({ id, checked, handleCheckboxChange, text }) => {
  return (
    <div className="d-flex column-gap-2">
      <Input
        className="m-0 border-secondary fs-6 rounded-1"
        type="checkbox"
        id={id}
        checked={checked}
        onChange={handleCheckboxChange}
      />
      <Label className="m-0 fw-bold" for={id}>
        {text}
      </Label>
    </div>
  );
};

export default OrderInfo;
